// src/components/DominiosNovosModal.js
// Sessão 5.0.1 — v2: curadoria com detecção de similaridade.
//
// Mudanças principais sobre v1:
//   - Quando há candidatos_match, banner amarelo alerta e destaca "Associar" como ação primária
//   - Quando não há match, mantém "Confirmar (criar)" como ação primária
//   - INSERT de empresa popula tanto company_name quanto empresa (campo curto do Dashboard)
//   - Usuário pode editar o nome curto (empresa) no modo "Editar e criar"
//   - Ao associar: atualiza empresa_id dos contatos órfãos do domínio (bônus)

import React, { useState, useMemo } from 'react';
import {
  X, CheckCircle2, Pencil, Link2, EyeOff, RefreshCw,
  AlertCircle, Search, AlertTriangle,
} from 'lucide-react';
import { useDominiosNovos } from '../hooks/useDominiosNovos';
import { supabase } from '../supabaseClient';

const TIPOS_EMPRESA = [
  { valor: 'developer',              rotulo: '🏗️ Incorporadora' },
  { valor: 'builder',                rotulo: '🏗️ Construtora' },
  { valor: 'investment_fund',        rotulo: '💰 Fundo de investimento' },
  { valor: 'real_estate_broker',     rotulo: '🏠 Corretora imobiliária' },
  { valor: 'law_firm',               rotulo: '⚖️ Advocacia' },
  { valor: 'architecture',           rotulo: '🏛️ Arquitetura' },
  { valor: 'engineering',            rotulo: '🔧 Engenharia' },
  { valor: 'consulting',             rotulo: '💼 Consultoria' },
  { valor: 'accounting',             rotulo: '📊 Contábil/Auditoria' },
  { valor: 'notary',                 rotulo: '📜 Cartório' },
  { valor: 'landowner',              rotulo: '🌾 Proprietária de terreno' },
  { valor: 'tool_saas',              rotulo: '🛠️ Ferramenta/SaaS' },
  { valor: 'other',                  rotulo: '❓ Outro' },
];

const formatDias = (dias) => {
  if (dias === null || dias === undefined) return '—';
  if (dias === 0) return 'hoje';
  if (dias < 30) return `${dias}d`;
  if (dias < 365) return `${Math.floor(dias / 30)}m`;
  return `${Math.floor(dias / 365)}a`;
};

// ============================================================================
// Sub-componente: linha de curadoria
// ============================================================================

const LinhaDominio = ({ dom, empresasExistentes, onConfirmar, onAssociar, onIgnorar, disabled }) => {
  const [modoEdicao, setModoEdicao] = useState(null);
  const [nomeEdit, setNomeEdit]   = useState(dom.sugestao_nome);
  const [curtoEdit, setCurtoEdit] = useState(dom.sugestao_empresa_curto);
  const [tipoEdit, setTipoEdit]   = useState(dom.sugestao_tipo);
  const [empresaAssociar, setEmpresaAssociar] = useState(
    dom.candidatos_match?.[0]?.empresa?.id || ''
  );
  const [filtroEmpresa, setFiltroEmpresa] = useState('');

  const temMatch = dom.candidatos_match && dom.candidatos_match.length > 0;
  const melhorMatch = dom.candidatos_match?.[0];

  const empresasFiltradas = useMemo(() => {
    if (!filtroEmpresa) return empresasExistentes.slice(0, 50);
    const f = filtroEmpresa.toLowerCase();
    return empresasExistentes
      .filter(e =>
        (e.company_name || '').toLowerCase().includes(f) ||
        (e.empresa || '').toLowerCase().includes(f)
      )
      .slice(0, 50);
  }, [empresasExistentes, filtroEmpresa]);

  const handleCriarRapido = () => {
    onConfirmar(dom, dom.sugestao_nome, dom.sugestao_empresa_curto, dom.sugestao_tipo);
  };

  const handleCriarEditado = () => {
    onConfirmar(dom, nomeEdit.trim(), curtoEdit.trim() || nomeEdit.trim(), tipoEdit);
    setModoEdicao(null);
  };

  const handleAssociarRapido = () => {
    if (melhorMatch) {
      onAssociar(dom, melhorMatch.empresa.id);
    }
  };

  const handleAssociarEscolhido = () => {
    if (!empresaAssociar) return;
    onAssociar(dom, empresaAssociar);
    setModoEdicao(null);
  };

  // Cor de fundo: amarelo se tem match (alerta), branco se não
  const bgCard = temMatch ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200';

  return (
    <li className={`border rounded-lg p-3 ${bgCard}`}>
      {/* Cabeçalho: domínio + métricas */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-slate-800 font-medium truncate" title={dom.dominio}>
            {dom.dominio}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
            <span className="font-semibold text-slate-700">{dom.qtd_emails} emails</span>
            <span>·</span>
            <span>from {dom.qtd_from} · to {dom.qtd_to} · cc {dom.qtd_cc}</span>
            <span>·</span>
            <span>{formatDias(dom.dias_desde_ultimo)}</span>
          </div>
        </div>
      </div>

      {/* BANNER DE ALERTA quando há possível duplicata */}
      {temMatch && modoEdicao === null && (
        <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-700" />
          <div className="text-xs text-amber-900 flex-1">
            <div className="font-semibold mb-0.5">Possível duplicata detectada:</div>
            {dom.candidatos_match.map((c, i) => (
              <div key={c.empresa.id} className={i === 0 ? 'font-semibold' : ''}>
                {i === 0 ? '→ ' : '  '}
                <span className="font-mono">{c.empresa.empresa || c.empresa.company_name}</span>
                <span className="text-amber-700">
                  {' '}({Math.round(c.confianca * 100)}% match)
                </span>
              </div>
            ))}
            <div className="mt-1 italic">
              Recomendado: <strong>Associar à empresa existente</strong>.
            </div>
          </div>
        </div>
      )}

      {/* Sugestão de criar (só quando NÃO há match) */}
      {!temMatch && modoEdicao === null && (
        <div className="mt-2 text-xs text-slate-600">
          Sugestão: criar <span className="font-semibold">{dom.sugestao_nome}</span>
          {dom.sugestao_empresa_curto !== dom.sugestao_nome &&
            <> (curto: <span className="font-semibold">{dom.sugestao_empresa_curto}</span>)</>
          }
          {' · '}
          {TIPOS_EMPRESA.find(t => t.valor === dom.sugestao_tipo)?.rotulo || dom.sugestao_tipo}
        </div>
      )}

      {/* Botões de ação (modo normal) */}
      {modoEdicao === null && (
        <div className="mt-2 flex gap-1 flex-wrap">
          {/* Ação primária: depende de temMatch */}
          {temMatch ? (
            <>
              <button
                onClick={handleAssociarRapido}
                disabled={disabled}
                className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50 font-semibold"
                title={`Associar a ${melhorMatch.empresa.empresa || melhorMatch.empresa.company_name}`}
              >
                <Link2 size={12} /> Associar a {melhorMatch.empresa.empresa || melhorMatch.empresa.company_name}
              </button>
              <button
                onClick={() => setModoEdicao('associar')}
                disabled={disabled}
                className="text-xs px-2 py-1.5 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 flex items-center gap-1 disabled:opacity-50"
                title="Escolher outra empresa"
              >
                Outra
              </button>
              <button
                onClick={() => setModoEdicao('criar')}
                disabled={disabled}
                className="text-xs px-2 py-1.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 flex items-center gap-1 disabled:opacity-50"
                title="Criar nova empresa mesmo assim (ignorando a sugestão de duplicata)"
              >
                <Pencil size={12} /> Criar nova
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleCriarRapido}
                disabled={disabled}
                className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1 disabled:opacity-50 font-semibold"
                title={`Criar "${dom.sugestao_nome}"`}
              >
                <CheckCircle2 size={12} /> Criar
              </button>
              <button
                onClick={() => setModoEdicao('criar')}
                disabled={disabled}
                className="text-xs px-2 py-1.5 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1 disabled:opacity-50"
                title="Editar nome/tipo antes de criar"
              >
                <Pencil size={12} /> Editar
              </button>
              <button
                onClick={() => setModoEdicao('associar')}
                disabled={disabled}
                className="text-xs px-2 py-1.5 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 flex items-center gap-1 disabled:opacity-50"
                title="Associar a empresa já cadastrada"
              >
                <Link2 size={12} /> Associar
              </button>
            </>
          )}
          <button
            onClick={() => onIgnorar(dom)}
            disabled={disabled}
            className="text-xs px-2 py-1.5 bg-slate-50 text-slate-500 rounded hover:bg-slate-100 flex items-center gap-1 disabled:opacity-50"
            title="Marcar como ignorado"
          >
            <EyeOff size={12} /> Ignorar
          </button>
        </div>
      )}

      {/* Formulário: editar e criar */}
      {modoEdicao === 'criar' && (
        <div className="mt-3 p-3 bg-emerald-50 rounded space-y-2">
          <div>
            <label className="text-xs text-slate-600 block mb-1">Nome completo (company_name)</label>
            <input
              type="text"
              value={nomeEdit}
              onChange={(e) => setNomeEdit(e.target.value)}
              className="w-full text-sm px-2 py-1 border rounded"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Nome curto (exibido no Dashboard)</label>
            <input
              type="text"
              value={curtoEdit}
              onChange={(e) => setCurtoEdit(e.target.value)}
              placeholder="Deixe em branco para usar o nome completo"
              className="w-full text-sm px-2 py-1 border rounded"
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 block mb-1">Tipo</label>
            <select
              value={tipoEdit}
              onChange={(e) => setTipoEdit(e.target.value)}
              className="w-full text-sm px-2 py-1 border rounded bg-white"
            >
              {TIPOS_EMPRESA.map(t => (
                <option key={t.valor} value={t.valor}>{t.rotulo}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setModoEdicao(null)}
              className="text-xs px-3 py-1.5 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleCriarEditado}
              disabled={!nomeEdit.trim() || disabled}
              className="text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              Criar
            </button>
          </div>
        </div>
      )}

      {/* Formulário: associar a existente */}
      {modoEdicao === 'associar' && (
        <div className="mt-3 p-3 bg-indigo-50 rounded space-y-2">
          <div>
            <label className="text-xs text-slate-600 block mb-1">
              Vincular <span className="font-mono">{dom.dominio}</span> à empresa:
            </label>
            <div className="relative mb-2">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={filtroEmpresa}
                onChange={(e) => setFiltroEmpresa(e.target.value)}
                placeholder="Filtrar empresas..."
                className="w-full text-sm pl-7 pr-2 py-1 border rounded"
                autoFocus
              />
            </div>
            <div className="border rounded bg-white max-h-48 overflow-auto divide-y divide-slate-100">
              {empresasFiltradas.length === 0 && (
                <div className="text-xs text-slate-400 italic p-3 text-center">
                  Nenhuma empresa encontrada.
                </div>
              )}
              {empresasFiltradas.map(e => {
                const isSelected = empresaAssociar === e.id;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => setEmpresaAssociar(e.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      isSelected
                        ? 'bg-indigo-100 text-indigo-900 font-semibold'
                        : 'hover:bg-slate-50 text-slate-800'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">
                        {e.company_name}
                        {e.empresa && e.empresa !== e.company_name && (
                          <span className="text-slate-500 text-xs ml-1">({e.empresa})</span>
                        )}
                      </span>
                      {isSelected && <span className="text-indigo-600 text-xs flex-shrink-0">✓</span>}
                    </div>
                  </button>
                );
              })}
            </div>
            {empresasFiltradas.length === 50 && (
              <div className="text-xs text-slate-500 italic mt-1">
                Mostrando primeiras 50. Refine o filtro.
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setModoEdicao(null); setFiltroEmpresa(''); }}
              className="text-xs px-3 py-1.5 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
            >
              Cancelar
            </button>
            <button
              onClick={handleAssociarEscolhido}
              disabled={!empresaAssociar || disabled}
              className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              Associar
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

// ============================================================================
// Modal principal
// ============================================================================

const DominiosNovosModal = ({ onClose, onDominioProcessado }) => {
  const { dominios, empresasExistentes, loading, erro, refresh } = useDominiosNovos({ minEmails: 2 });
  const [processando, setProcessando] = useState(false);
  const [mensagem, setMensagem] = useState(null);
  const [filtroTexto, setFiltroTexto] = useState('');

  const dominiosFiltrados = useMemo(() => {
    if (!filtroTexto.trim()) return dominios;
    const f = filtroTexto.toLowerCase();
    return dominios.filter(d =>
      d.dominio.includes(f) ||
      d.sugestao_nome.toLowerCase().includes(f) ||
      d.sugestao_tipo.includes(f) ||
      d.candidatos_match?.some(c =>
        (c.empresa.company_name || '').toLowerCase().includes(f) ||
        (c.empresa.empresa || '').toLowerCase().includes(f)
      )
    );
  }, [dominios, filtroTexto]);

  const qtdComMatch = useMemo(
    () => dominios.filter(d => d.candidatos_match?.length > 0).length,
    [dominios]
  );

  // === Ação: criar empresa NOVA + vincular domínio ===
  const handleConfirmar = async (dom, nome, curto, tipo) => {
    setProcessando(true);
    setMensagem(null);
    try {
      const { data: novaEmpresa, error: e1 } = await supabase
        .from('empresas')
        .insert({
          company_name: nome,
          empresa: curto || nome,
          ramo: tipo === 'advocacia' ? 'advocacia' :
                tipo === 'incorporadora' ? 'incorporadora' :
                tipo === 'construtora' ? 'construtora' :
                tipo === 'consultoria' ? 'consultoria' :
                tipo === 'fundo_investimento' ? 'asset_management' : null,
          notas_estrategicas: `Cadastrada via A2 curadoria (${new Date().toISOString().slice(0,10)}) a partir do domínio ${dom.dominio}.`,
        })
        .select('id, empresa')
        .single();
      if (e1) throw e1;

      const { error: e2 } = await supabase.from('dominios_empresa').insert({
        dominio: dom.dominio,
        empresa_id: novaEmpresa.id,
        tipo_empresa: tipo,
        confianca: 0.9,
        criado_por: 'humano:eduardo@astoncapital.com.br (via_mcp:A2_curadoria_v2)',
        observacao: `Criado via A2 v2: empresa nova "${nome}" a partir de ${dom.qtd_emails} emails detectados.`,
      });
      if (e2) throw e2;

      setMensagem({ tipo: 'sucesso', texto: `✅ ${nome} criada e vinculada a ${dom.dominio}.` });
      onDominioProcessado?.();
      refresh();
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: `Erro: ${err.message || err}` });
    } finally {
      setProcessando(false);
    }
  };

  // === Ação: associar domínio a empresa existente + atualizar contatos órfãos ===
  const handleAssociar = async (dom, empresaId) => {
    setProcessando(true);
    setMensagem(null);
    try {
      const empresaNome = empresasExistentes.find(e => e.id === empresaId)?.company_name || 'empresa';

      const { error: e1 } = await supabase.from('dominios_empresa').insert({
        dominio: dom.dominio,
        empresa_id: empresaId,
        tipo_empresa: dom.sugestao_tipo,
        confianca: 0.85,
        criado_por: 'humano:eduardo@astoncapital.com.br (via_mcp:A2_curadoria_v2)',
        observacao: `Associação manual via A2 v2: ${dom.dominio} → ${empresaNome}`,
      });
      if (e1) throw e1;

      // BÔNUS: atualizar contatos órfãos cujo email é do mesmo domínio
      // (só os que estão com empresa_id NULL — não sobrescrevemos decisões anteriores)
      const { count: contatosAtualizados, error: e2 } = await supabase
        .from('contatos')
        .update({ empresa_id: empresaId }, { count: 'exact' })
        .is('empresa_id', null)
        .ilike('email', `%@${dom.dominio}`);
      // Nota: .ilike com LIKE padrão, não quebra se tiver 0 matches

      if (e2) {
        // Não falha a operação por causa disso, só loga
        console.warn('Falha ao atualizar contatos órfãos:', e2);
      }

      const extra = contatosAtualizados > 0 ? ` + ${contatosAtualizados} contato(s) órfão(s) vinculado(s)` : '';
      setMensagem({
        tipo: 'sucesso',
        texto: `✅ ${dom.dominio} vinculado a ${empresaNome}${extra}.`
      });
      onDominioProcessado?.();
      refresh();
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: `Erro: ${err.message || err}` });
    } finally {
      setProcessando(false);
    }
  };

  // === Ação: ignorar domínio ===
  const handleIgnorar = async (dom) => {
    setProcessando(true);
    setMensagem(null);
    try {
      const { error } = await supabase.from('dominios_empresa').insert({
        dominio: dom.dominio,
        empresa_id: null,
        tipo_empresa: 'ignored',
        confianca: 1.0,
        criado_por: 'humano:eduardo@astoncapital.com.br (via_mcp:A2_curadoria_v2)',
        observacao: 'Marcado como ignorado via A2 v2: não apresentar em futuras curadorias.',
      });
      if (error) throw error;

      setMensagem({ tipo: 'sucesso', texto: `🙈 ${dom.dominio} ignorado.` });
      onDominioProcessado?.();
      refresh();
    } catch (err) {
      setMensagem({ tipo: 'erro', texto: `Erro: ${err.message || err}` });
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center pt-8 px-4">
      <div className="bg-slate-50 rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white rounded-t-lg">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Curadoria</div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              🔍 Domínios novos no mailing
              {!loading && dominios.length > 0 && (
                <span className="text-xs font-normal bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                  {dominios.length}
                </span>
              )}
              {!loading && qtdComMatch > 0 && (
                <span className="text-xs font-normal bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <AlertTriangle size={10} /> {qtdComMatch} possível(is) duplicata(s)
                </span>
              )}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Domínios com ≥ 2 emails, sem relação cadastrada.
              {qtdComMatch > 0 && ' Linhas amarelas = alta chance de já existir cadastrada.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} disabled={loading || processando} className="text-slate-400 hover:text-slate-600 p-1">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filtro */}
        <div className="px-4 py-2 border-b bg-white">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              placeholder="Filtrar por domínio, nome sugerido, tipo ou empresa candidata..."
              className="w-full text-sm pl-9 pr-3 py-2 border border-slate-200 rounded"
            />
          </div>
        </div>

        {/* Mensagem */}
        {mensagem && (
          <div className={`px-4 py-2 text-sm ${
            mensagem.tipo === 'sucesso'
              ? 'bg-emerald-50 text-emerald-800 border-b border-emerald-200'
              : 'bg-red-50 text-red-800 border-b border-red-200'
          }`}>
            {mensagem.texto}
          </div>
        )}

        {/* Corpo */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="text-sm text-slate-500 py-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
              Varrendo emails e checando duplicatas...
            </div>
          )}

          {!loading && erro && (
            <div className="text-sm text-red-600 bg-red-50 rounded p-3 flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>Erro ao carregar: {erro.message || String(erro)}</span>
            </div>
          )}

          {!loading && !erro && dominiosFiltrados.length === 0 && (
            <div className="text-sm text-slate-500 italic py-8 text-center bg-white rounded border border-slate-200">
              {filtroTexto
                ? `Nenhum domínio bate com "${filtroTexto}".`
                : 'Nenhum domínio novo detectado. Tudo já cadastrado ✨'}
            </div>
          )}

          {!loading && !erro && dominiosFiltrados.length > 0 && (
            <ul className="space-y-2">
              {dominiosFiltrados.map(dom => (
                <LinhaDominio
                  key={dom.dominio}
                  dom={dom}
                  empresasExistentes={empresasExistentes}
                  onConfirmar={handleConfirmar}
                  onAssociar={handleAssociar}
                  onIgnorar={handleIgnorar}
                  disabled={processando}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-white rounded-b-lg text-xs text-slate-500 flex items-center justify-between">
          <span>
            {dominiosFiltrados.length}
            {filtroTexto && ` de ${dominios.length}`} domínio{dominiosFiltrados.length === 1 ? '' : 's'}
          </span>
          <span>Linhas amarelas = possível duplicata. Associar é a ação recomendada.</span>
        </div>
      </div>
    </div>
  );
};

export default DominiosNovosModal;
